import React, { useState } from 'react';

const ConceptBlueprintModal = ({ isOpen, onClose, conceptBlueprint }) => {
    const [expandedSections, setExpandedSections] = useState({});

    if (!isOpen || !conceptBlueprint || !conceptBlueprint.concept) {
        return null;
    }

    const concept = conceptBlueprint.concept;

    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflow: 'auto'
    };

    const modalContentStyle = {
        backgroundColor: '#1f1f1f',
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
    };

    const titleStyle = {
        fontSize: '24px',
        fontWeight: '700',
        color: '#ffffff',
        margin: 0
    };

    const closeButtonStyle = {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#9a9a9a',
        fontSize: '24px',
        cursor: 'pointer',
        padding: '0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        transition: 'all 0.2s ease'
    };

    const sectionCardStyle = {
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(139, 92, 246, 0.04))',
        border: '1px solid rgba(139, 92, 246, 0.25)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
    };

    const sectionHeaderStyle = {
        fontSize: '18px',
        fontWeight: '600',
        color: '#e0e0e0',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        userSelect: 'none'
    };

    const subsectionStyle = {
        marginBottom: '16px',
        paddingLeft: '16px',
        borderLeft: '2px solid rgba(139, 92, 246, 0.3)'
    };

    const labelStyle = {
        fontSize: '12px',
        color: '#9a9a9a',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '6px',
        fontWeight: '600'
    };

    const valueStyle = {
        fontSize: '14px',
        color: '#e0e0e0',
        lineHeight: '1.6',
        wordBreak: 'break-word'
    };

    const listItemStyle = {
        marginBottom: '8px',
        paddingLeft: '16px',
        position: 'relative'
    };

    const listItemBulletStyle = {
        position: 'absolute',
        left: 0,
        color: '#8b5cf6'
    };

    const renderSection = (title, content, sectionKey) => {
        const isExpanded = expandedSections[sectionKey] !== false;
        return (
            <div key={sectionKey} style={sectionCardStyle}>
                <div
                    style={sectionHeaderStyle}
                    onClick={() => toggleSection(sectionKey)}
                >
                    <span>{title}</span>
                    <span style={{ fontSize: '12px', color: '#9a9a9a' }}>
                        {isExpanded ? '▼' : '▶'}
                    </span>
                </div>
                {isExpanded && (
                    <div>
                        {content}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h2 style={titleStyle}>Concept Blueprint</h2>
                    <button
                        style={closeButtonStyle}
                        onClick={onClose}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#2d2d2d';
                            e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#9a9a9a';
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Website Overview */}
                {concept.website_overview && renderSection(
                    'Website Overview',
                    <div>
                        {concept.website_overview.current_state_summary && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Current State Summary</div>
                                <div style={valueStyle}>{concept.website_overview.current_state_summary}</div>
                            </div>
                        )}
                        {concept.website_overview.target_audience && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Target Audience</div>
                                <div style={valueStyle}>{concept.website_overview.target_audience}</div>
                            </div>
                        )}
                        {concept.website_overview.core_objectives && Array.isArray(concept.website_overview.core_objectives) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Core Objectives</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.website_overview.core_objectives.map((objective, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{objective}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.website_overview.brand_identity && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Brand Identity</div>
                                <div style={valueStyle}>{concept.website_overview.brand_identity}</div>
                            </div>
                        )}
                    </div>,
                    'website_overview'
                )}

                {/* Information Architecture */}
                {concept.information_architecture && renderSection(
                    'Information Architecture',
                    <div>
                        {concept.information_architecture.proposed_navigation_structure && Array.isArray(concept.information_architecture.proposed_navigation_structure) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Proposed Navigation Structure</div>
                                {concept.information_architecture.proposed_navigation_structure.map((navItem, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: '#2d2d2d',
                                        border: '1px solid #404040',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '6px' }}>
                                            {navItem.section_name}
                                        </div>
                                        {navItem.description && (
                                            <div style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '6px' }}>
                                                {navItem.description}
                                            </div>
                                        )}
                                        {navItem.priority && (
                                            <span style={{
                                                backgroundColor: navItem.priority === 'primary' ? '#10b981' : navItem.priority === 'secondary' ? '#f59e0b' : '#6b7280',
                                                color: '#ffffff',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                marginRight: '8px'
                                            }}>
                                                {navItem.priority}
                                            </span>
                                        )}
                                        {navItem.subsections && Array.isArray(navItem.subsections) && navItem.subsections.length > 0 && (
                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#9a9a9a' }}>
                                                Subsections: {navItem.subsections.join(', ')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {concept.information_architecture.content_hierarchy && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Content Hierarchy</div>
                                <div style={valueStyle}>{concept.information_architecture.content_hierarchy}</div>
                            </div>
                        )}
                        {concept.information_architecture.key_pages && Array.isArray(concept.information_architecture.key_pages) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Key Pages ({concept.information_architecture.key_pages.length})</div>
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {concept.information_architecture.key_pages.map((page, idx) => (
                                        <div key={idx} style={{
                                            backgroundColor: '#2d2d2d',
                                            border: '1px solid #404040',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '10px'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                                                    {page.page_name}
                                                </div>
                                                {page.priority && (
                                                    <span style={{
                                                        backgroundColor: page.priority === 'critical' ? '#ef4444' : page.priority === 'important' ? '#f59e0b' : '#6b7280',
                                                        color: '#ffffff',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: '600'
                                                    }}>
                                                        {page.priority}
                                                    </span>
                                                )}
                                            </div>
                                            {page.purpose && (
                                                <div style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '6px' }}>
                                                    <strong>Purpose:</strong> {page.purpose}
                                                </div>
                                            )}
                                            {page.content_requirements && Array.isArray(page.content_requirements) && page.content_requirements.length > 0 && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Content Requirements:</div>
                                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#c0c0c0' }}>
                                                        {page.content_requirements.slice(0, 5).map((req, reqIdx) => (
                                                            <li key={reqIdx}>{req}</li>
                                                        ))}
                                                        {page.content_requirements.length > 5 && (
                                                            <li style={{ color: '#9a9a9a', fontStyle: 'italic' }}>
                                                                +{page.content_requirements.length - 5} more...
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>,
                    'information_architecture'
                )}

                {/* Content Strategy */}
                {concept.content_strategy && renderSection(
                    'Content Strategy',
                    <div>
                        {concept.content_strategy.content_voice_tone && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Voice & Tone</div>
                                <div style={valueStyle}>{concept.content_strategy.content_voice_tone}</div>
                            </div>
                        )}
                        {concept.content_strategy.key_messages && Array.isArray(concept.content_strategy.key_messages) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Key Messages</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.content_strategy.key_messages.map((message, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{message}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.content_strategy.content_types && Array.isArray(concept.content_strategy.content_types) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Content Types</div>
                                {concept.content_strategy.content_types.map((type, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: '#2d2d2d',
                                        border: '1px solid #404040',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '6px' }}>
                                            {type.content_type}
                                        </div>
                                        {type.purpose && (
                                            <div style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '6px' }}>
                                                <strong>Purpose:</strong> {type.purpose}
                                            </div>
                                        )}
                                        {type.recommendation && (
                                            <div style={{ fontSize: '13px', color: '#9a9a9a' }}>
                                                <strong>Recommendation:</strong> {type.recommendation}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {concept.content_strategy.content_gaps && Array.isArray(concept.content_strategy.content_gaps) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Content Gaps</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.content_strategy.content_gaps.map((gap, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{gap}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>,
                    'content_strategy'
                )}

                {/* User Experience */}
                {concept.user_experience && renderSection(
                    'User Experience',
                    <div>
                        {concept.user_experience.user_journeys && Array.isArray(concept.user_experience.user_journeys) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>User Journeys</div>
                                {concept.user_experience.user_journeys.map((journey, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: '#2d2d2d',
                                        border: '1px solid #404040',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '6px' }}>
                                            {journey.journey_name}
                                        </div>
                                        {journey.description && (
                                            <div style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '8px' }}>
                                                {journey.description}
                                            </div>
                                        )}
                                        {journey.key_steps && Array.isArray(journey.key_steps) && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Key Steps:</div>
                                                <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#c0c0c0' }}>
                                                    {journey.key_steps.map((step, stepIdx) => (
                                                        <li key={stepIdx}>{step}</li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}
                                        {journey.optimization_opportunities && Array.isArray(journey.optimization_opportunities) && (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Optimization Opportunities:</div>
                                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#c0c0c0' }}>
                                                    {journey.optimization_opportunities.map((opp, oppIdx) => (
                                                        <li key={oppIdx}>{opp}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {concept.user_experience.interaction_patterns && Array.isArray(concept.user_experience.interaction_patterns) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Interaction Patterns</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.user_experience.interaction_patterns.map((pattern, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{pattern}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.user_experience.accessibility_requirements && Array.isArray(concept.user_experience.accessibility_requirements) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Accessibility Requirements</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.user_experience.accessibility_requirements.map((req, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{req}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.user_experience.mobile_experience && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Mobile Experience</div>
                                <div style={valueStyle}>{concept.user_experience.mobile_experience}</div>
                            </div>
                        )}
                    </div>,
                    'user_experience'
                )}

                {/* Design Direction */}
                {concept.design_direction && renderSection(
                    'Design Direction',
                    <div>
                        {concept.design_direction.design_style && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Design Style</div>
                                <div style={valueStyle}>{concept.design_direction.design_style}</div>
                            </div>
                        )}
                        {concept.design_direction.color_palette_recommendations && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Color Palette Recommendations</div>
                                <div style={valueStyle}>{concept.design_direction.color_palette_recommendations}</div>
                            </div>
                        )}
                        {concept.design_direction.typography_recommendations && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Typography Recommendations</div>
                                <div style={valueStyle}>{concept.design_direction.typography_recommendations}</div>
                            </div>
                        )}
                        {concept.design_direction.visual_elements && Array.isArray(concept.design_direction.visual_elements) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Visual Elements</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.design_direction.visual_elements.map((element, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{element}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.design_direction.layout_principles && Array.isArray(concept.design_direction.layout_principles) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Layout Principles</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.design_direction.layout_principles.map((principle, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{principle}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>,
                    'design_direction'
                )}

                {/* Functional Requirements */}
                {concept.functional_requirements && renderSection(
                    'Functional Requirements',
                    <div>
                        {concept.functional_requirements.core_features && Array.isArray(concept.functional_requirements.core_features) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Core Features</div>
                                {concept.functional_requirements.core_features.map((feature, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: '#2d2d2d',
                                        border: '1px solid #404040',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                                                {feature.feature_name}
                                            </div>
                                            {feature.priority && (
                                                <span style={{
                                                    backgroundColor: feature.priority === 'must_have' ? '#ef4444' : feature.priority === 'should_have' ? '#f59e0b' : '#6b7280',
                                                    color: '#ffffff',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600'
                                                }}>
                                                    {feature.priority.replace('_', ' ')}
                                                </span>
                                            )}
                                        </div>
                                        {feature.description && (
                                            <div style={{ fontSize: '13px', color: '#c0c0c0' }}>
                                                {feature.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {concept.functional_requirements.forms_interactions && Array.isArray(concept.functional_requirements.forms_interactions) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Forms & Interactions</div>
                                {concept.functional_requirements.forms_interactions.map((form, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: '#2d2d2d',
                                        border: '1px solid #404040',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '6px' }}>
                                            {form.form_type}
                                        </div>
                                        {form.purpose && (
                                            <div style={{ fontSize: '13px', color: '#c0c0c0', marginBottom: '8px' }}>
                                                <strong>Purpose:</strong> {form.purpose}
                                            </div>
                                        )}
                                        {form.requirements && Array.isArray(form.requirements) && (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Requirements:</div>
                                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#c0c0c0' }}>
                                                    {form.requirements.map((req, reqIdx) => (
                                                        <li key={reqIdx}>{req}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {concept.functional_requirements.integrations && Array.isArray(concept.functional_requirements.integrations) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Integrations</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {concept.functional_requirements.integrations.map((integration, idx) => (
                                        <span key={idx} style={{
                                            backgroundColor: '#2d2d2d',
                                            border: '1px solid #404040',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            color: '#e0e0e0'
                                        }}>
                                            {integration}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>,
                    'functional_requirements'
                )}

                {/* Redesign Priorities */}
                {concept.redesign_priorities && renderSection(
                    'Redesign Priorities',
                    <div>
                        {concept.redesign_priorities.critical_improvements && Array.isArray(concept.redesign_priorities.critical_improvements) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Critical Improvements</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.redesign_priorities.critical_improvements.map((improvement, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{improvement}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.redesign_priorities.quick_wins && Array.isArray(concept.redesign_priorities.quick_wins) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Quick Wins</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.redesign_priorities.quick_wins.map((win, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{win}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.redesign_priorities.long_term_goals && Array.isArray(concept.redesign_priorities.long_term_goals) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Long Term Goals</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.redesign_priorities.long_term_goals.map((goal, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{goal}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {concept.redesign_priorities.success_metrics && Array.isArray(concept.redesign_priorities.success_metrics) && (
                            <div style={subsectionStyle}>
                                <div style={labelStyle}>Success Metrics</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                                    {concept.redesign_priorities.success_metrics.map((metric, idx) => (
                                        <li key={idx} style={listItemStyle}>
                                            <span style={listItemBulletStyle}>•</span>
                                            <span>{metric}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>,
                    'redesign_priorities'
                )}
            </div>
        </div>
    );
};

export default ConceptBlueprintModal;

